'use client';

import { supabase } from '@/lib/db/supabase';
import type { User } from '@supabase/supabase-js';
import {
  RegisterData,
  UpdateProfileData,
  UserData,
  Profile,
  AuthResult
} from './types';

export class AuthService {
  async register(data: RegisterData): Promise<AuthResult<User>> {
    try {
      console.log('📝 Iniciando registro:', { 
        ...data, 
        password: '[REDACTED]' 
      });

      // 1. Verificar se o email já existe
      const { data: emailExists, error: checkError } = await supabase
        .rpc('check_email_exists', { email_address: data.email });

      if (checkError) {
        console.error('❌ Erro ao verificar email:', checkError);
        return {
          success: false,
          error: 'Erro ao verificar disponibilidade do email. Por favor, tente novamente.'
        };
      }

      if (emailExists) {
        return {
          success: false,
          error: 'Este email já está cadastrado. Por favor, use outro email ou faça login.'
        };
      }

      // 2. Registrar usuário
      const { data: auth, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.name
          }
        }
      });

      if (signUpError) {
        console.error('❌ Erro no signup:', signUpError);
        
        // Traduzir erros comuns do Supabase
        if (signUpError.message.includes('Password should be at least')) {
          return {
            success: false,
            error: 'A senha deve ter no mínimo 6 caracteres'
          };
        }
        if (signUpError.message.includes('Invalid email')) {
          return {
            success: false,
            error: 'Email inválido. Por favor, verifique se digitou corretamente'
          };
        }
        
        return {
          success: false,
          error: signUpError.message
        };
      }

      if (!auth.user) {
        return {
          success: false,
          error: 'Não foi possível criar a conta. Por favor, tente novamente'
        };
      }

      console.log('✅ Usuário criado:', auth.user.id);

      // Fazer login automaticamente após o registro
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });

      if (signInError) {
        console.error('❌ Erro no login automático:', signInError);
        return {
          success: true,
          data: auth.user,
          error: 'Conta criada com sucesso, mas houve um erro no login automático. Por favor, faça login manualmente.',
        };
      }

      // Aguardar a sessão ser estabelecida
      await new Promise(resolve => setTimeout(resolve, 1000));

      return {
        success: true,
        data: auth.user
      };
    } catch (error) {
      console.error('❌ Erro no registro:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro inesperado ao criar conta. Por favor, tente novamente'
      };
    }
  }

  async checkEmailExists(email: string): Promise<AuthResult<boolean>> {
    try {
      const { data, error } = await supabase
        .rpc('check_email_exists', { email_address: email });

      if (error) {
        console.error('❌ Erro ao verificar email:', error);
        return {
          success: false,
          error: 'Não foi possível verificar a disponibilidade do email'
        };
      }

      return {
        success: true,
        data: !!data
      };
    } catch (error) {
      console.error('❌ Erro ao verificar email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao verificar disponibilidade do email',
        data: false
      };
    }
  }

  async updateProfile(userId: string, data: UpdateProfileData): Promise<AuthResult> {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('❌ Erro ao atualizar perfil:', error);
        return {
          success: false,
          error: 'Não foi possível atualizar o perfil. Por favor, tente novamente'
        };
      }

      return { success: true };
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro inesperado ao atualizar perfil'
      };
    }
  }

  async getProfile(userId: string): Promise<AuthResult<Profile>> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('❌ Erro ao buscar perfil:', error);
        return {
          success: false,
          error: 'Não foi possível carregar os dados do perfil'
        };
      }

      if (!data) {
        return {
          success: false,
          error: 'Perfil não encontrado'
        };
      }

      return { 
        success: true,
        data
      };
    } catch (error) {
      console.error('❌ Erro ao buscar perfil:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Erro ao carregar dados do perfil'
      };
    }
  }

  async getUserData(userId: string): Promise<AuthResult<UserData>> {
    try {
      // Primeiro buscar os dados do perfil
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('name, email, phone, level')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('❌ Erro ao buscar dados do perfil:', profileError);
        return {
          success: false,
          error: 'Não foi possível carregar os dados do usuário'
        };
      }

      // Se não encontrou o perfil, buscar da sessão do usuário
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('❌ Erro ao buscar sessão:', sessionError);
        return {
          success: false,
          error: 'Não foi possível verificar a sessão do usuário'
        };
      }

      const user = session?.user;
      if (!user) {
        return {
          success: false,
          error: 'Usuário não autenticado'
        };
      }

      return {
        success: true,
        data: {
          name: profile?.name || user.user_metadata?.full_name || '',
          email: profile?.email || user.email || '',
          phone: profile?.phone || user.phone || '',
          level: profile?.level || 'free'
        }
      };
    } catch (error) {
      console.error('❌ Erro ao buscar dados do usuário:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao carregar dados do usuário'
      };
    }
  }
}

// Exporta uma instância única do serviço
export default new AuthService();